import dayjs, { type Dayjs } from "dayjs";
import useSWR from "swr";
import { useText, type MeetingKey } from "./text";
import {
  type AnnualWheelEvent,
  type AnnualWheelEventColor,
  type AnnualWheelEventData,
} from "./types";

interface StubMeeting {
  meeting: MeetingKey;
  color: AnnualWheelEventColor;
  days: string[];
}

// A board that meets six times a year, and a few meetings of its committees
const MEETINGS: StubMeeting[] = [
  {
    meeting: "board",
    color: "blue",
    days: ["02-12", "04-23", "06-11", "09-17", "10-29", "12-10"],
  },
  { meeting: "audit", color: "purple", days: ["02-05", "09-10"] },
  { meeting: "risk", color: "pink", days: ["03-19", "11-26"] },
  { meeting: "remuneration", color: "yellow", days: ["01-29"] },
  { meeting: "annualGeneral", color: "red", days: ["05-14"] },
];

// The made-up events have the meeting they are instead of a name, so that the
// name follows the language; an event someone adds or renames has a name of its own
type StoredEvent = Omit<AnnualWheelEvent, "name"> &
  ({ name: string } | { meeting: MeetingKey });

// The same days every year would put some of the meetings on a weekend
const toWeekday = (date: Dayjs) => {
  if (date.day() === 6) return date.subtract(1, "day");

  return date.day() === 0 ? date.add(1, "day") : date;
};

let lastId = 0;
// Kept in memory only, so a reload brings the made-up events back
let storedEvents: StoredEvent[] | undefined;

const seedEvents = () => {
  const thisYear = dayjs().year();

  return [thisYear - 1, thisYear, thisYear + 1].flatMap((year) =>
    MEETINGS.flatMap(({ meeting, color, days }) =>
      days.map((day): StoredEvent => ({
        id: ++lastId,
        meeting,
        date: toWeekday(dayjs(`${year}-${day}`)).format("YYYY-MM-DD"),
        color,
      })),
    ),
  );
};

const allEvents = () => (storedEvents ??= seedEvents());

// Stubbed: nothing is stored anywhere, so the events live in this module
export const useAnnualWheelEvents = () => {
  const text = useText();
  const { data, mutate } = useSWR("annual-wheel-prototype/events", () =>
    Promise.resolve([...allEvents()]),
  );

  return {
    data: data?.map((event): AnnualWheelEvent => ({
      id: event.id,
      date: event.date,
      color: event.color,
      name: "meeting" in event ? text.meetings[event.meeting] : event.name,
    })),
    mutate,
  };
};

export const createAnnualWheelEvent = (data: AnnualWheelEventData) => {
  storedEvents = [...allEvents(), { ...data, id: ++lastId }];

  return Promise.resolve();
};

export const updateAnnualWheelEvent = (
  id: number,
  data: AnnualWheelEventData,
) => {
  storedEvents = allEvents().map((storedEvent) =>
    storedEvent.id === id ? { ...data, id } : storedEvent,
  );

  return Promise.resolve();
};

export const deleteAnnualWheelEvent = (id: number) => {
  storedEvents = allEvents().filter((storedEvent) => storedEvent.id !== id);

  return Promise.resolve();
};
